/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Screen } from '../../core/navigation/Screen';
import { ScreenController } from '../../core/navigation/ScreenController';

interface IScreenProperties<T> {
    identifier?: string;
    properties?: T;
}

const useScreenController = <T>({ identifier, properties }: IScreenProperties<T>) => {
    const location = useLocation();
    const url = location.pathname + location.search;
    const oScreenController: ScreenController = ScreenController.getInstance();
    const [screen, setScreen] = useState<Screen<T>>(new Screen<T>(identifier ?? url));

    const handleStackChange = () => {};

    useEffect(() => {
        oScreenController.addStackChangeListener(handleStackChange);

        // Use window.location.origin as base for relative path from useLocation hook
        const oUrl = new URL(url, window.location.origin);

        if (properties || oUrl.searchParams.size > 0) {
            screen.setProperties({
                url,
                ...(properties || {}),
                ...Object.fromEntries(oUrl.searchParams),
            } as T);
        }

        oScreenController.push(screen);
        setScreen(oScreenController.getScreen());

        return () => {
            oScreenController.removeStackChangeListener(handleStackChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        screen,
    };
};

export default useScreenController;
